/**
 * ──────────────────────────────────────────────
 * CART DRAWER — CUSTOM LOGIC OVERVIEW
 * ──────────────────────────────────────────────
 * This file includes critical overrides to:
 * 
 * ✅ Prevent drawer from closing when last item is removed
 * ✅ Ensure fallback CTA displays and stays inside the drawer
 * ✅ Lock drawer open/closed state with `drawerState` system
 * ✅ Preserve focus traps and animations during AJAX section updates
 * ✅ Restore close behavior after cart updates to avoid state bleed
 * ✅ Show/hide fallback CTA strictly based on drawer state
 * 
 * 🔐 DO NOT REMOVE OR OVERRIDE:
 *  - `drawer._originalClose` reference
 *  - `drawer.close = () => { ... }` override logic
 *  - `CartDrawerItems.updateQuantity()` interception
 *  - `requestAnimationFrame(... showFallbackCTA())` hooks
 * 
 * 🧼 IMPORTANT FOR MAINTENANCE:
 *  - Any updates to Shopify cart-drawer section must preserve
 *    #CartDrawer structure, `.cart-item` class, and fallback CTA container.
 *  - Any new JavaScript that modifies the drawer DOM should dispatch
 *    `document.dispatchEvent(new CustomEvent('cart:updated'))`
 */

// 🌍 Dynamic upsell drawer selection
let upsellDrawer = null;
const upsellDrawerUS = document.getElementById('membership-upsell-drawer');
const upsellDrawerINTL = document.getElementById('membership-upsell-drawer-intl');
const isUS = Shopify && Shopify.country === 'US';
upsellDrawer = isUS ? upsellDrawerUS : upsellDrawerINTL;

// ✅ Fallback CTA visibility helpers
function evaluateCTAVisibility(forceShow = false) {
  const fallbackContainers = document.querySelectorAll('.fallback-cta');
  const cartDrawer = document.querySelector('cart-drawer');
  const isCartOpen = cartDrawer?.classList.contains('active');
  const shouldShow = forceShow || isCartOpen;

  fallbackContainers.forEach(container => {
    container.classList.toggle('fallback-ready', shouldShow);
    container.style.setProperty('display', shouldShow ? 'block' : 'none', 'important');
  });

  console.info('[CTA] Visibility evaluated:', shouldShow);
}

function hideFallbackCTA() {
  document.querySelectorAll('.fallback-cta').forEach(container => {
    container.classList.remove('fallback-ready');
    container.style.setProperty('display', 'none', 'important');
  });
}

function moveFallbackCTAIntoDrawer() {
  const fallbackCTA = document.getElementById('fallback-cta');
  const drawerInner = document.querySelector('cart-drawer .drawer__inner');

  if (!fallbackCTA || !drawerInner) return;

  if (!drawerInner.contains(fallbackCTA)) {
    document.querySelectorAll('#fallback-cta').forEach(el => {
      if (!drawerInner.contains(el)) el.remove();
    });

    drawerInner.appendChild(fallbackCTA);
    fallbackCTA.style.setProperty('display', 'none', 'important');
  }

  evaluateCTAVisibility();
}

// 🧼 Inject style to forcibly hide fallback CTA immediately (before DOMContentLoaded)
(() => {
  const style = document.createElement('style');
  style.textContent = `#fallback-cta { display: none !important; }`;
  document.head.appendChild(style);
})();

// 🧼 Global cleanup on navigation/unload
window.addEventListener('cart:closed', () => {
  document.body.classList.remove('overflow-hidden');
});

window.addEventListener('beforeunload', () => {
  document.body.classList.remove('overflow-hidden');
});

// 🛠️ Patch drawer.close globally to always remove overflow-hidden
requestAnimationFrame(() => {
  const drawer = document.querySelector('cart-drawer');
  if (drawer && drawer.close) {
    const originalClose = drawer.close.bind(drawer);
    drawer.close = function () {
      originalClose();
      document.body.classList.remove('overflow-hidden');
    };
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  if (drawer._patched) return;
  drawer._patched = true;

  let allowClose = false;
  drawer._originalClose = drawer.close.bind(drawer);

  drawer.close = function () {
    if (allowClose) {
      this._originalClose();
      hideFallbackCTA();
    } else {
      console.info('[CartDrawer] Prevented close');
    }
  };

  drawer.addEventListener('allow-close', () => {
    allowClose = true;
    setTimeout(() => { allowClose = false; }, 500);
  });

  const closeBtn = drawer.querySelector('.drawer__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      drawer.dispatchEvent(new Event('allow-close'));
    });
  }

  moveFallbackCTAIntoDrawer();

  // 👁️ Monitor drawer visibility changes and hide fallback CTA when closed
  const drawerObserver = new MutationObserver(() => {
    const isOpen = drawer.classList.contains('active');
    if (!isOpen) {
      console.info('[CartDrawer] Observer detected drawer close — hiding fallback CTA');
      hideFallbackCTA();
    }
  });
  drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });

  // 🚫 Force-close the drawer on page load (if open) to hide fallback CTA safely
  if (drawer.classList.contains('active')) {
    requestAnimationFrame(() => {
      console.info('[CartDrawer] Force-closing drawer on page load to hide fallback CTA');
      drawer.close();
    });
  }
});

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => this.classList.add('animate', 'active'));

    this.addEventListener(
      'transitionend',
      () => {
        const container = this.querySelector('.drawer__inner') || document.getElementById('CartDrawer');
        const focusEl = container || this.querySelector('.drawer__close');
        trapFocus(container, focusEl);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // ✅ Sync fallback CTA on open
    evaluateCTAVisibility(true);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    // ✅ Hide fallback CTA on close — now wrapped in requestAnimationFrame
    requestAnimationFrame(() => {
      hideFallbackCTA();
    });
  }

  setSummaryAccessibility(summaryEl) {
    summaryEl.setAttribute('role', 'button');
    summaryEl.setAttribute('aria-expanded', 'false');

    const siblingId = summaryEl.nextElementSibling?.getAttribute('id');
    if (siblingId) {
      summaryEl.setAttribute('aria-controls', siblingId);
    }

    summaryEl.addEventListener('click', (event) => {
      const isOpen = event.currentTarget.closest('details').hasAttribute('open');
      event.currentTarget.setAttribute('aria-expanded', !isOpen);
    });

    summaryEl.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;
    const wasOpen = this.classList.contains('active');

    this.getSectionsToRender().forEach(({ id, selector }) => {
      const sectionHTML = parsedState.sections[id];
      const targetElement = selector ? document.querySelector(selector) : document.getElementById(id);

      if (!sectionHTML || !targetElement) return;

      const newContent = this.getSectionInnerHTML(sectionHTML, selector);
      if (!newContent) return;

      targetElement.innerHTML = newContent;

      if (id === 'cart-drawer' && typeof showFallbackCTA === 'function') {
        requestAnimationFrame(() => {
          console.info('[CartDrawer] Forcing CTA display after injection');
          showFallbackCTA();
        });
      }
    });

    requestAnimationFrame(() => {
      const observer = new MutationObserver(() => {
        const drawerEl = document.querySelector('cart-drawer');
        if (!drawerEl) return;

        const cartCount = parseInt(drawerEl.querySelector('#cart-count')?.textContent || '0', 10);
        drawerEl.syncVisibility();

        if (!drawerEl._originalClose) {
          drawerEl._originalClose = drawerEl.close.bind(drawerEl);
        }

        if (drawerEl.shouldPreventCloseOnce) {
          drawerEl.close = () => {
            console.info('[CartDrawer] Prevented drawer from closing on empty cart update');
          };
          drawerEl.shouldPreventCloseOnce = false;
        } else {
          drawerEl.close = drawerEl._originalClose;
        }

        if (typeof showFallbackCTA === 'function') {
          requestAnimationFrame(() => {
            console.info('[CartDrawer] Forcing CTA display');
            showFallbackCTA();
          });
        }

        if (wasOpen) {
          drawerEl.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
        }

        moveFallbackCTAIntoDrawer();

        observer.disconnect();
      });

      observer.observe(this, { childList: true, subtree: true });
    });
  }

  syncVisibility() {
    const container = this;
    if (!container) return;

    const itemRows = container.querySelectorAll('.cart-item');
    const hasItems = itemRows.length > 0;

    const emptyMessage = container.querySelector('.drawer__empty-message');
    const tableWrapper = container.querySelector('.drawer__cart-table');
    const checkoutBtn = container.querySelector('#CartDrawer-Checkout');
    const footer = container.querySelector('.drawer__footer');
    const countBubble = document.querySelector('.cart-count-bubble');

    if (emptyMessage) emptyMessage.classList.toggle('hidden', hasItems);
    if (tableWrapper) tableWrapper.classList.toggle('hidden', !hasItems);
    if (footer) footer.classList.remove('hidden');
    if (checkoutBtn) checkoutBtn.disabled = !hasItems;
    if (countBubble) countBubble.style.display = hasItems ? 'inline-block' : 'none';
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const found = doc.querySelector(selector);
    if (!found) {
      console.warn(`[CartDrawer] Could not find selector "${selector}" in section HTML.`);
      return null;
    }
    return found.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#shopify-section-cart-drawer'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#cart-icon-bubble'
      }
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateWithParsedSections(sections) {
    const wasOpen = this.classList.contains('active');
    const drawer = this;

    this.renderContents({ id: null, sections });

    requestAnimationFrame(() => {
      const freshDrawer = document.querySelector('cart-drawer');
      if (!freshDrawer) return;

      if (wasOpen) {
        console.info('[CartDrawer] Re-opening drawer after refresh');
        freshDrawer.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
      }

      if (typeof showFallbackCTA === 'function') {
        showFallbackCTA();
      }

      moveFallbackCTAIntoDrawer();
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', (event) => {
      const input = event.target.closest('input[name="updates[]"]');
      if (!input) return;
      this.updateQuantity(input.dataset.index, input.value);
    });
  }

  updateQuantity(lineIndex, quantity) {
    const body = JSON.stringify({
      line: parseInt(lineIndex, 10),
      quantity: parseInt(quantity, 10)
    });
    const cartDrawer = document.querySelector('cart-drawer');
    const wasOpen = cartDrawer?.classList.contains('active');

    const cartItems = document.querySelectorAll('.cart-item');
    const isLastItem = cartItems.length === 1 && quantity === 0;

    if (isLastItem && cartDrawer) {
      if (!cartDrawer._originalClose) {
        cartDrawer._originalClose = cartDrawer.close.bind(cartDrawer);
      }

      cartDrawer.close = () => {
        console.info('[CartDrawer] Intercepted close() on last item');
      };
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body
    })
      .then(res => {
        if (!res.ok) throw new Error('Cart quantity update failed: ' + res.status);
        return res.json();
      })
      .then(cart =>
        fetch('/?sections=cart-drawer,cart-icon-bubble')
          .then(res => {
            if (!res.ok) throw new Error('Cart section refresh failed: ' + res.status);
            return res.json();
          })
          .then(sections => ({ cart, sections }))
      )
      .then(({ cart, sections }) => {
        const activeDrawer = document.querySelector('cart-drawer');
        if (!activeDrawer) throw new Error('Native cart drawer is unavailable');

        activeDrawer.updateWithParsedSections(sections);

        if (cartDrawer?._originalClose) {
          cartDrawer.close = cartDrawer._originalClose;
          delete cartDrawer._originalClose;
        }

        const refreshedDrawer = document.querySelector('cart-drawer');
        if (wasOpen && refreshedDrawer) {
          if (typeof refreshedDrawer.open === 'function') refreshedDrawer.open();
          else {
            refreshedDrawer.classList.add('animate', 'active');
            document.body.classList.add('overflow-hidden');
          }
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { itemCount: cart.item_count }
        }));
      })
      .catch(error => {
        console.error('[CartDrawer] Quantity update failed:', error);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('shopify:section:load', (e) => {
  if (e.detail.sectionId === 'cart-drawer') {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }
});
